<template lang="pug">
.page.page-lifetree
  .container.py-5
    h1.page-title {{ $t('lifetreePage.title') }}
    p.page-subtitle {{ $t('lifetreePage.subtitle') }}

    p.intro {{ $t('lifetreePage.intro') }}

    .legend
      span.legend-item
        span.legend-dot.dot-chose
        | {{ $t('lifetreePage.legendChose') }}
      span.legend-item
        span.legend-dot.dot-alt
        | {{ $t('lifetreePage.legendAlt') }}

    .lifetree-list
      .lifetree-node(v-for="(n, i) in nodes", :key="i", :class="`theme-${n.theme}`")
        .node-header
          span.node-year {{ n.year }}{{ n.month ? '/' + n.month : '' }}
          span.node-age {{ $t('lifetreePage.age') }} {{ n.age }}
          span.tag.node-theme {{ themeLabel(n.theme) }}
        h3.node-label(v-if="n.label") {{ n.label }}
        p.node-scene {{ n.scene }}
        .node-chose.soft-panel(v-if="n.chose")
          .chose-marker ✓ {{ $t('lifetreePage.he chose') }}
          .chose-label {{ n.chose.label }}
          p.chose-conseq {{ n.chose.consequence }}
        .node-alts.soft-panel(v-if="n.alternatives && n.alternatives.length")
          .alts-marker ↪ {{ $t('lifetreePage.didNotChoose') }}
          .alt-row(v-for="(alt, j) in n.alternatives", :key="j")
            .alt-label {{ alt.label }}
              span.tag.alt-tag(v-if="alt.plausibility === 'speculative'") {{ $t('lifetreePage.speculative') }}
            p.alt-note {{ alt.note }}

    .footer-back
      router-link.back-link(:to="localizedPath('/about')") ← {{ $t('lifetreePage.backToAbout') }}
    site-links
</template>

<script>
import SiteLinks from '@/components/ui/SiteLinks.vue'
import { withSlash } from '@/runtime/site-path.mjs'
import { useI18n } from 'vue-i18n'

export default {
  components: { SiteLinks },
  name: 'PageLifetree',
  setup() {
    const { locale } = useI18n()
    const localizedPath = (path) => withSlash(locale.value === 'zh' ? '/zh' + path : path)
    return { localizedPath }
  },
  computed: {
    nodes() { return this.$tm('lifetreePage.nodes') || [] },
    themesMap() {
      const arr = this.$tm('lifetreePage.themes') || []
      const map = {}
      arr.forEach((t) => { map[t.id] = t.label })
      return map
    }
  },
  methods: {
    themeLabel(themeId) { return this.themesMap[themeId] || themeId }
  }
}
</script>

<style lang="sass">
.page-lifetree
  background-color: var(--bg0)
  color: var(--ink)
  min-height: 100vh

  .container
    max-width: var(--editorial-w)

  .page-title
    font-size: 2rem
    text-align: center
    margin-bottom: 8px

  .page-subtitle
    text-align: center
    color: var(--ink-3)
    margin-bottom: 24px
    font-size: 1.05rem

  .intro
    text-align: center
    color: var(--ink-3)
    max-width: 700px
    margin: 0 auto 32px auto
    line-height: 1.6
    font-size: 0.95rem

  .legend
    display: flex
    justify-content: center
    gap: 24px
    margin-bottom: 48px
    color: var(--ink-3)
    font-size: 0.9rem

  .legend-item
    display: flex
    align-items: center
    gap: 8px

  .legend-dot
    display: inline-block
    width: 10px
    height: 10px
    border-radius: 50%
    &.dot-chose
      background: var(--k-sketch)
    &.dot-alt
      background: var(--ink-4)

  .lifetree-list
    display: flex
    flex-direction: column
    gap: 24px

  // 時間線節點：無框面板；主題用一顆色點標（.tag 的 --tag-c），不畫左線
  .lifetree-node
    --tag-c: var(--k-archive)
    border-radius: var(--r-lg)
    padding: 28px 32px
    background: var(--bg1)
    transition: var(--t-hover)
    &.theme-engineer-artist
      --tag-c: var(--k-td)
    &.theme-teaching
      --tag-c: var(--k-teaching)
    &.theme-chaos-system
      --tag-c: var(--k-lab)
    &.theme-personal-public
      --tag-c: var(--k-music)
    &:hover
      background: var(--bg2)
    @media (max-width: 575px)
      padding: 22px 20px

  .node-header
    display: flex
    gap: 16px
    align-items: center
    flex-wrap: wrap
    margin-bottom: 8px

  .node-year
    font-size: 1.2rem
    font-weight: 700
    color: var(--ink)

  .node-age
    font-size: 0.85rem
    color: var(--ink-4)

  .node-label
    font-size: 1.3rem
    color: var(--ink)
    margin: 8px 0 12px 0

  .node-scene
    color: var(--ink-2)
    font-style: italic
    line-height: 1.6
    margin-bottom: 16px

  .node-chose
    margin-bottom: 16px
    background: var(--tint-2)

  .chose-marker
    font-size: 0.85rem
    color: var(--k-sketch)
    text-transform: uppercase
    letter-spacing: 0.1em
    margin-bottom: 6px

  .chose-label
    font-size: 1.05rem
    color: var(--ink)
    font-weight: 600
    margin-bottom: 8px

  .chose-conseq
    color: var(--ink-2)
    line-height: 1.6
    margin: 0

  .alts-marker
    font-size: 0.85rem
    color: var(--ink-3)
    text-transform: uppercase
    letter-spacing: 0.1em
    margin-bottom: 8px

  .alt-row
    margin-bottom: 12px
    &:last-child
      margin-bottom: 0

  .alt-label
    font-size: 1rem
    color: var(--ink-2)
    margin-bottom: 4px

  .alt-tag
    margin-left: 10px
    vertical-align: middle
    --tag-c: var(--ink-4)

  .alt-note
    color: var(--ink-3)
    font-size: 0.9rem
    line-height: 1.5
    margin: 0

  .footer-back
    text-align: center
    margin-top: 48px

</style>
